import React from "react";
import { OverlayTrigger, Popover, Table } from "react-bootstrap";
import { GLOBAL_CONSTANTS } from "../../constants";

function DeliveryTooltip({ delivery, categoriesToInclude = ["os", "jbi", "gaz", "ner", "mix", "other", "pilmat", "grandline", "metall"] }) {
  if (!delivery) {
    console.log("❌ DeliveryTooltip: Нет данных о доставке", { delivery, categoriesToInclude });
    return <span>Нет данных</span>;
  }

  const validDeliveries = categoriesToInclude
    .map((category) => {
      const categoryData = delivery[category];
      const details = categoryData?.details;
      if (!details) {
        // console.log(`⚠️ Нет данных для категории ${category}`);
        return null;
      }

      // Проверяем, является ли details массивом (как для jbi)
      if (Array.isArray(details)) {
        if (details.length === 0) {
          // console.log(`⚠️ Пустой массив details для категории ${category}`);
          return null;
        }
        const totalCost = details.reduce((sum, group) => {
          const cost = group?.deliveryData?.data?.totalCost || 0;
          // console.log(`📦 ${category} - Группа ${group?.groupKey}: totalCost=${cost}`);
          return sum + cost;
        }, 0);
        const firstGroup = details[0];
        return {
          category,
          data: {
            bestMethod: firstGroup?.deliveryData?.data?.bestMethod || "—",
            distanceProdToClient: firstGroup?.deliveryData?.data?.distanceProdToClient || "—",
            numberOfTrips: firstGroup?.deliveryData?.data?.numberOfTrips || "—",
            costPerTrip: firstGroup?.deliveryData?.data?.costPerTrip || "—",
            totalCost,
            calculationMode: firstGroup?.deliveryData?.data?.calculationMode || null,
            fallbackReason: firstGroup?.deliveryData?.data?.fallbackReason || null,
          },
        };
      }

      // Проверяем, есть ли в details поле data (как в ответе от сервера)
      let detailData = details?.data || details;

      // Временное логирование для отладки ПРАЙС ГРАНДЛАЙН
      if (category === "Металлочерепица, профлист и штакетник") {
        console.log(`🔍 DeliveryTooltip DEBUG для ${category}:`, { details, detailData });
      }

      if (!detailData) {
        // console.log(`⚠️ Нет detailData для категории ${category}`, details);
        return null;
      }

      // Формируем объект данных
      const deliveryData = {
        bestMethod: detailData.bestMethod || "—",
        distanceProdToClient: detailData.distanceProdToClient || "—",
        numberOfTrips: detailData.numberOfTrips || "—",
        costPerTrip: detailData.costPerTrip || "—",
        totalCost: detailData.totalCost || 0, // Учитываем totalCost = 0 как валидное значение
        calculationMode: detailData.calculationMode || null,
        fallbackReason: detailData.fallbackReason || null,
      };

      // console.log(`📋 Данные для категории ${category}:`, deliveryData);

      return {
        category,
        data: deliveryData,
      };
    })
    .filter((item) => {
      if (!item || !item.data) {
        // console.log(`⚠️ Фильтрация: Пропущен элемент`, item);
        return false;
      }
      // Убираем фильтрацию по totalCost, так как 0 — валидное значение
      return true;
    });

  if (validDeliveries.length === 0) {
    console.log("❌ DeliveryTooltip: Нет валидных данных о доставке", { validDeliveries });
    return <span>Нет данных</span>;
  }

  const totalDeliveryCost = validDeliveries.reduce(
    (sum, item) => {
      const cost = item.data.totalCost || 0;
      // console.log(`📦 Итоговая стоимость для ${item.category}: ${cost}`);
      return sum + cost;
    },
    0
  );

  // console.log(`📦 Общая стоимость доставки: ${totalDeliveryCost}`);

  const popover = (
    <Popover
      id="delivery-popover"
      style={{
        maxWidth: "min(90vw, 920px)",
        minWidth: "720px",
        maxHeight: "70vh",
        overflow: "auto",
      }}
    >
      <Popover.Header as="h5">Детали доставки</Popover.Header>
      <Popover.Body>
        <div style={{ display: "block", maxWidth: "100%", overflowX: "auto" }}>
          <Table striped bordered hover size="sm" style={{ width: "100%", tableLayout: "auto" }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: "nowrap" }}>Тип</th>
                <th style={{ whiteSpace: "nowrap" }}>Метод</th>
                <th style={{ whiteSpace: "nowrap" }}>Км</th>
                <th style={{ whiteSpace: "nowrap" }}>Рейсов</th>
                <th style={{ whiteSpace: "nowrap" }}>Цена</th>
                <th style={{ whiteSpace: "nowrap" }}>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {validDeliveries.map((item, index) => (
                <tr key={index}>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {GLOBAL_CONSTANTS.CATEGORY_NAMES[item.category] || "-"}
                  </td>
                  <td>{item.data.bestMethod || "Не указано"}</td>
                  <td style={{ whiteSpace: "nowrap", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {typeof item.data.distanceProdToClient === "number"
                      ? item.data.distanceProdToClient.toLocaleString()
                      : "—"}
                  </td>
                  <td style={{ whiteSpace: "nowrap", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {item.data.numberOfTrips?.toLocaleString() || "—"}
                  </td>
                  <td style={{ whiteSpace: "nowrap", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {item.data.costPerTrip?.toLocaleString() || "—"}
                  </td>
                  <td style={{ whiteSpace: "nowrap", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {item.data.totalCost?.toLocaleString() || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger={["hover", "focus"]}
      placement="right"
      flip
      overlay={popover}
      delay={{ show: 120, hide: 100 }}
      popperConfig={{
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 10],
            },
          },
        ],
      }}
    >
      <span
        style={{
          cursor: "pointer",
          textDecoration: "underline",
          color: "#007bff",
        }}
      >
        {totalDeliveryCost >= 0 ? totalDeliveryCost.toLocaleString() : "Нет данных"}
      </span>
    </OverlayTrigger>
  );
}

export default DeliveryTooltip;
